import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { _PortfolioProduct, _single } from "../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { BasicPage } from "../../../../theme/Component/BasicPage";
import { BasicButton } from "../../../../theme/Component/BasicButton";
import { BasicTable } from "../../../../theme/Component/BasicTable";
import { ListMenu } from "../../../../theme/Component/ListMenu";
import { Modal } from "../../../../theme/Component/Modal";
import { ModalConfirm } from "../../../../theme/Component/ModalConfirm";
import useModal, { MODAL_LG, MODAL_md, MODAL_SM } from "../../../../theme/Component/hooks/useModal";
import { PortfolioProductCapability } from "./PortfolioProductCapability/PortfolioProductCapability";

interface PortfolioProductProps {
    isModal?: boolean;
    _onChange?: (value: any) => void;
}

export const PortfolioProduct = ({ isModal = false, _onChange }: PortfolioProductProps) => {
    const { instance, bean, setBean, lista, setLista, handleChange, addRequiredFields, validateForm, isMissing, isRequired, } = useDataBean(_PortfolioProduct);

    const [search, setSearch] = useState("");
    const modalForm = useModal("Producto del portafolio", MODAL_md);
    const modalCapabilities = useModal("Capacidades del producto de portafolio", MODAL_LG);
    const modalDelete = useModal("Eliminar producto del portafolio", MODAL_SM);

    useEffect(() => {
        addRequiredFields(["Name", "SerialName", "Description"]);
        getList();
    }, []);

    const getList = () => {
        instance.getPortfolioProductCatalogPorPropiedades({ State: 1 }, null).then(
            (response: any) => setLista(response ?? [])
        ).catch(() => toast.error("No se pudo cargar el portafolio de productos"));
    };

    const create = () => {
        setBean({
            Name: "",
            SerialName: "",
            Description: "",
            IDEmployee: _single.getAccountID(),
            State: 1,
        });
        modalForm.open("Agregar producto al portafolio");
    };

    const save = () => {
        if (!validateForm()) {
            toast.error("Faltan campos obligatorios por diligenciar");
            return;
        }

        instance.updatePortfolioProduct({ ...bean, IDEmployee: bean.IDEmployee ?? _single.getAccountID(), State: bean.State ?? 1, }).then(
            () => {
                toast.success(bean.IDPortfolioProduct ? "Producto actualizado" : "Producto creado");
                modalForm.close();
                getList();
            }
        ).catch((error: any) => toast.error(error));
    };

    const remove = () => {
        instance.updatePortfolioProduct({ ...bean, IDEmployee: _single.getAccountID(), State: 2, }).then(
            () => {
                toast.success("Producto eliminado");
                modalDelete.close();
                getList();
            }
        ).catch((error: any) => toast.error(error));
    };

    const selectRow = (value: any) => {
        setBean(value);
        _onChange?.(value);
    };

    const head = [
        { title: "ID", property: "IDPortfolioProduct", visible: true },
        { title: "Nombre", property: "Name", visible: true },
        { title: "Nombre serial", property: "SerialName", visible: true },
        { title: "Descripción", property: "Description", visible: true },
    ];

    const iconButtons = [
        { nombre: "Agregar", icono: "ri-file-add-line", visible: _single.canCreate(), evento: create },
    ];

    const rowActions = () => (
        <ListMenu listaBotones={[
            { titulo: "Editar", icono: "ri-edit-2-line", visible: _single.canEdit(), evento: () => modalForm.open(`Editar ${bean.Name ?? "producto"}`), },
            { titulo: "Capacidades", icono: "ri-list-check-2", visible: bean.IDPortfolioProduct != null, evento: () => modalCapabilities.open(`Capacidades de ${bean.Name ?? "producto"}`), },
            { titulo: "Eliminar", icono: "ri-delete-bin-line", visible: _single.canDelete(), evento: () => modalDelete.open(), },
        ]} />
    );

    const content = (
        <div className="row">
            <div className="col-md-12">
                <BasicTable body={lista} head={head} rowSet={selectRow} listButtons={rowActions()} iconButtons={iconButtons} />
            </div>

            <Modal modal={modalForm.modal} updateModal={modalForm.setModal} eventModal={save}>
                <div className="row">
                    <div className="col-md-6">
                        <label className={isMissing("Name") ? "has-error" : ""}>
                            Nombre {isRequired("Name") && <span className="text-danger">*</span>}
                        </label>
                        <input name="Name" className="form-control" value={bean.Name ?? ""} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                        <label className={isMissing("SerialName") ? "has-error" : ""}>
                            Nombre serial {isRequired("SerialName") && <span className="text-danger">*</span>}
                        </label>
                        <input name="SerialName" className="form-control" value={bean.SerialName ?? ""} onChange={handleChange} />
                    </div>
                    <div className="col-md-12">
                        <label className={isMissing("Description") ? "has-error" : ""}>
                            Descripción {isRequired("Description") && <span className="text-danger">*</span>}
                        </label>
                        <textarea name="Description" className="form-control" value={bean.Description ?? ""} onChange={handleChange} />
                    </div>
                </div>
            </Modal>

            <Modal modal={modalCapabilities.modal} updateModal={modalCapabilities.setModal}>
                <PortfolioProductCapability IDPortfolioProduct={bean.IDPortfolioProduct} isModal />
            </Modal>

            <ModalConfirm modal={modalDelete.modal} updateModal={modalDelete.setModal} eventModal={remove}>
                <strong>¿Eliminar el producto {bean.Name}?</strong>
            </ModalConfirm>
        </div>
    );

    return isModal ? content : (
        <BasicPage titulo="Portafolio de productos">
            {content}
        </BasicPage>
    );
};
